import { useState, useEffect, useRef, Fragment, ReactNode } from 'react';
import { apiRequest, getAccessToken, isTokenExpired, refreshAccessToken } from '../utils/auth';
import { FiTrash2, FiImage, FiType, FiGrid, FiChevronUp, FiChevronDown, FiEdit2, FiVideo, FiPlus, FiX } from 'react-icons/fi';
import RichTextEditor from './RichTextEditor';
import { useEscapeToClose } from '../utils/useEscapeToClose';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
// The title field and its translations modal reuse the home layout's Section
// Title styles (hl-title-btn, hl-label-modal, …) so both look the same
import '../pages/HomeLayoutPage.css';

type SectionType = 'image' | 'paragraph' | 'extensionCard' | 'creatorCard' | 'video';

/** Article text in every language but English, keyed by app language code. */
type Translations = Record<string, string>;

/** The app's supported languages — the same list the home layout's Section Title offers. */
const LABEL_LANGUAGES: { code: string; name: string; required?: boolean }[] = [
  { code: 'en', name: 'English', required: true },
  { code: 'zh-hk', name: 'Traditional Chinese (Hong Kong)' },
  { code: 'zh-tw', name: 'Traditional Chinese (Taiwan)' },
  { code: 'zh-cn', name: 'Simplified Chinese' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
];

/** Languages a title or paragraph can be translated into — English is the base text. */
const TRANSLATION_LANGUAGES = LABEL_LANGUAGES.filter((lang) => lang.code !== 'en');

/** Trims each translation and drops blank ones; undefined when none are left. */
const cleanTranslations = (map?: Translations): Translations | undefined => {
  if (!map) return undefined;
  const out: Translations = {};
  for (const lang of TRANSLATION_LANGUAGES) {
    const value = (map[lang.code] || '').trim();
    if (value) out[lang.code] = value;
  }
  return Object.keys(out).length > 0 ? out : undefined;
};

/** Whether rich-text HTML has no visible text (an emptied editor leaves `<br>` or `&nbsp;`). */
const isBlankHtml = (html: string) =>
  !html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

interface CoverImage {
  type: 'articleCoverImage';
  imageKey: string;
  imageUrl?: string;
  videoKey?: string;
  videoUrl?: string;
  thumbnailKey?: string;
  thumbnailUrl?: string;
  /** English title — required, and what the app shows for a language with no translation. */
  title?: string;
  /** Non-English titles keyed by app language code; never carries `en`. */
  titleTranslations?: Translations;
  aspectRatio?: number;
  /** Video covers: set by the upload probe, and only then. The app hides its mute
   *  control when it is false, and keeps showing it when the field is absent. */
  hasAudio?: boolean;
}

interface ImageSection {
  type: 'image';
  imageKey: string;
  imageUrl?: string;
  imageKey2?: string;
  imageUrl2?: string;
  title?: string;
  aspectRatio?: number;
}

interface ParagraphSection {
  type: 'paragraph';
  /** English HTML — required, and the fallback for every other language. */
  content: string;
  /**
   * Non-English HTML keyed by app language code. A language is present (even as
   * '') once the admin adds it with +, which is what gives it an editor.
   */
  contentTranslations?: Translations;
}

interface ExtensionCardSection {
  type: 'extensionCard';
  extensionId: string;
  extensionName?: string;
  extensionCreator?: string;
  imageKey?: string;
  imageUrl?: string;
}

interface CreatorCardSection {
  type: 'creatorCard';
  creatorId: string;
  creatorName?: string;
}

interface VideoSection {
  type: 'video';
  videoKey: string;
  videoUrl?: string;
  thumbnailKey?: string;
  thumbnailUrl?: string;
  aspectRatio?: number;
  /** Set by the upload probe; see CoverImage.hasAudio. */
  hasAudio?: boolean;
}

type ArticleSection = ImageSection | ParagraphSection | ExtensionCardSection | CreatorCardSection | VideoSection;
type AllSectionTypes = CoverImage | ArticleSection;

interface Article {
  _id: string;
  articleId: string;
  title: string;
  creator?: string;
  sections: AllSectionTypes[];
  isPublished: boolean;
  startDate?: string | null;
  endDate?: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ExtensionSearchResult {
  _id: string;
  Name: string;
  Creator: string;
  thumbnailImageUrl?: string;
}

interface CreatorSearchResult {
  userId: string;
  name: string;
  avatar?: string;
}

/** Section types offered in the left panel; dragged onto the article or clicked to append. */
const SECTION_OPTIONS: { type: SectionType; label: string; icon: ReactNode }[] = [
  { type: 'image', label: 'Image', icon: <FiImage /> },
  { type: 'paragraph', label: 'Paragraph', icon: <FiType /> },
  { type: 'extensionCard', label: 'Lens', icon: <FiGrid /> },
  { type: 'creatorCard', label: 'Creator', icon: <FiEdit2 /> },
  { type: 'video', label: 'Video', icon: <FiVideo /> },
];

/** Firefox won't start a drag unless dataTransfer carries something; a private MIME type
    rather than text/plain, so a stray drop on one of the modal's many text inputs can't
    paste the payload as text. */
const DRAG_MIME = 'application/x-fraw-article-section';

/** A new section type coming from the left panel, or an existing section being reordered. */
type DragState =
  | { kind: 'new'; sectionType: SectionType }
  | { kind: 'reorder'; index: number };

interface ArticleFormModalProps {
  isOpen: boolean;
  editingArticle: Article | null;
  onClose: () => void;
  onSuccess: () => void;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

export default function ArticleFormModal({ isOpen, editingArticle, onClose, onSuccess }: ArticleFormModalProps) {
  const [formSections, setFormSections] = useState<AllSectionTypes[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  
  const [extensionSearchQuery, setExtensionSearchQuery] = useState('');
  const [extensionSearchResults, setExtensionSearchResults] = useState<ExtensionSearchResult[]>([]);
  const [searchingExtensions, setSearchingExtensions] = useState(false);
  const [activeExtensionSectionIndex, setActiveExtensionSectionIndex] = useState<number | null>(null);
  
  const [creatorSearchQuery, setCreatorSearchQuery] = useState('');
  const [creatorSearchResults, setCreatorSearchResults] = useState<CreatorSearchResult[]>([]);
  const [searchingCreators, setSearchingCreators] = useState(false);
  const [activeCreatorSectionIndex, setActiveCreatorSectionIndex] = useState<number | null>(null);
  
  const [dragging, setDragging] = useState<DragState | null>(null);
  // Where the dragged section will land, as a gap index into formSections: gap 1
  // sits just below the cover, gap formSections.length at the end of the list.
  const [dropGap, setDropGap] = useState<number | null>(null);
  const [uploadingImageIndex, setUploadingImageIndex] = useState<number | null>(null);
  const [uploadingImage2Index, setUploadingImage2Index] = useState<number | null>(null);
  const [uploadingVideoIndex, setUploadingVideoIndex] = useState<number | null>(null);
  const [coverMediaType, setCoverMediaType] = useState<'image' | 'video'>('image');
  // The article title's translations modal, and the title as it stood when the
  // modal opened: its inputs write straight into the cover, so Cancel puts this back
  const [titleEditorOpen, setTitleEditorOpen] = useState(false);
  const titleSnapshot = useRef<{ title?: string; titleTranslations?: Translations }>({});
  // Which paragraph (section index) has its "add a language" menu open
  const [languagePickerIndex, setLanguagePickerIndex] = useState<number | null>(null);
  // The open menu's wrapper (button + menu), for click-outside, and the menu itself
  const languagePickerRef = useRef<HTMLDivElement>(null);
  const languageMenuRef = useRef<HTMLDivElement>(null);

  // Escape behaves like the Cancel button — including staying put mid-save.
  useEscapeToClose(isOpen, () => {
    if (!submitting) onClose();
  });
  // The title translations modal sits above the form, so it takes Escape first
  useEscapeToClose(titleEditorOpen, () => cancelTitleEditor());
  // An open language menu closes on Escape before the form does
  useEscapeToClose(languagePickerIndex !== null, () => setLanguagePickerIndex(null));

  // The language menu closes on a click anywhere outside it, and scrolls into
  // view when it opens — under the last paragraph it would land below the fold
  useEffect(() => {
    if (languagePickerIndex === null) return;
    languageMenuRef.current?.scrollIntoView({ block: 'nearest' });
    const handleMouseDown = (e: MouseEvent) => {
      if (!languagePickerRef.current?.contains(e.target as Node)) setLanguagePickerIndex(null);
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [languagePickerIndex]);

  useEffect(() => {
    if (isOpen) {
      if (editingArticle) {
        setFormSections(editingArticle.sections);
        setStartDate(editingArticle.startDate ? new Date(editingArticle.startDate) : null);
        setEndDate(editingArticle.endDate ? new Date(editingArticle.endDate) : null);
        // Derive cover media type from existing data
        const cover = editingArticle.sections[0];
        if (cover?.type === 'articleCoverImage' && (cover as CoverImage).videoKey) {
          setCoverMediaType('video');
        } else {
          setCoverMediaType('image');
        }
      } else {
        setFormSections([{ type: 'articleCoverImage', imageKey: '', videoKey: '' }]);
        setStartDate(null);
        setEndDate(null);
        setCoverMediaType('image');
      }
    }
  }, [isOpen, editingArticle]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (extensionSearchQuery) {
        searchExtensions(extensionSearchQuery);
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [extensionSearchQuery]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (creatorSearchQuery) {
        searchCreators(creatorSearchQuery);
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [creatorSearchQuery]);

  const searchExtensions = async (query: string) => {
    if (query.length < 2) {
      setExtensionSearchResults([]);
      return;
    }
    try {
      setSearchingExtensions(true);
      const response = await apiRequest<{ success: boolean; data: ExtensionSearchResult[] }>(
        `/api/admin/notifications/search-extensions?q=${encodeURIComponent(query)}`
      );
      if (response.success) {
        setExtensionSearchResults(response.data);
      }
    } catch (err) {
      console.error('Error searching extensions:', err);
    } finally {
      setSearchingExtensions(false);
    }
  };

  const searchCreators = async (query: string) => {
    if (query.length < 2) {
      setCreatorSearchResults([]);
      return;
    }
    try {
      setSearchingCreators(true);
      const response = await apiRequest<{ success: boolean; data: CreatorSearchResult[] }>(
        `/api/admin/notifications/search-users?q=${encodeURIComponent(query)}`
      );
      if (response.success) {
        setCreatorSearchResults(response.data);
      }
    } catch (err) {
      console.error('Error searching creators:', err);
    } finally {
      setSearchingCreators(false);
    }
  };

  const resetForm = () => {
    setFormSections([{ type: 'articleCoverImage', imageKey: '', videoKey: '' }]);
    setStartDate(null);
    setEndDate(null);
    setExtensionSearchQuery('');
    setExtensionSearchResults([]);
    setActiveExtensionSectionIndex(null);
    setCreatorSearchQuery('');
    setCreatorSearchResults([]);
    setActiveCreatorSectionIndex(null);
    setTitleEditorOpen(false);
    setLanguagePickerIndex(null);
  };

  const createSection = (type: SectionType): ArticleSection => {
    switch (type) {
      case 'image':
        return { type: 'image', imageKey: '', title: '' };
      case 'paragraph':
        return { type: 'paragraph', content: '' };
      case 'extensionCard':
        return { type: 'extensionCard', extensionId: '' };
      case 'creatorCard':
        return { type: 'creatorCard', creatorId: '' };
      case 'video':
        return { type: 'video', videoKey: '' };
    }
  };

  /** Inserts a section at a gap index; appends when no position is given. */
  const addSection = (type: SectionType, at?: number) => {
    setFormSections((prev) => {
      const next = [...prev];
      // Never above the cover
      next.splice(Math.max(1, at ?? next.length), 0, createSection(type));
      return next;
    });
  };

  const removeSection = (index: number) => {
    if (index === 0) return;
    setLanguagePickerIndex(null);
    setFormSections(formSections.filter((_, i) => i !== index));
  };

  const updateSection = (index: number, updates: any) => {
    setFormSections(formSections.map((section, i) => 
      i === index ? { ...section, ...updates } as AllSectionTypes : section
    ));
  };

  const moveSection = (index: number, direction: 'up' | 'down') => {
    if (index === 0) return;
    setLanguagePickerIndex(null);
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex === 0 || newIndex >= formSections.length) return;
    
    const newSections = [...formSections];
    [newSections[index], newSections[newIndex]] = [newSections[newIndex], newSections[index]];
    setFormSections(newSections);
  };

  // ---------------------------------------------------------------------------
  // Drag & drop: a new section type from the left panel, or an in-list reorder.
  // Both resolve to a gap index, drawn as a horizontal line between sections.
  // ---------------------------------------------------------------------------

  const handleNewDragStart = (e: React.DragEvent, type: SectionType) => {
    e.dataTransfer.setData(DRAG_MIME, type);
    e.dataTransfer.effectAllowed = 'copy';
    setDragging({ kind: 'new', sectionType: type });
  };

  const handleReorderStart = (e: React.DragEvent, index: number) => {
    if (index === 0) return;
    e.dataTransfer.setData(DRAG_MIME, String(index));
    e.dataTransfer.effectAllowed = 'move';
    setDragging({ kind: 'reorder', index });
  };

  const setGap = (gap: number) => {
    if (!dragging) return;
    // Dropping into the gap just above or below itself is a no-op — show no line
    if (dragging.kind === 'reorder' && (gap === dragging.index || gap === dragging.index + 1)) {
      setDropGap(null);
    } else {
      setDropGap(gap);
    }
  };

  /** Hovering a section's top half targets the gap above it, the bottom half the gap below. */
  const handleSectionDragOver = (e: React.DragEvent, index: number) => {
    if (!dragging || index === 0) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    setGap(e.clientY < rect.top + rect.height / 2 ? index : index + 1);
  };

  const handleZoneDragOver = (e: React.DragEvent, gap: number) => {
    if (!dragging) return;
    e.preventDefault();
    e.stopPropagation();
    setGap(gap);
  };

  const handleZoneDragLeave = (gap: number) => {
    if (dragging?.kind === 'new' && dropGap === gap) setDropGap(null);
  };

  /** New sections commit on drop; reorders commit on drag end. */
  const handleDrop = (e: React.DragEvent, gap: number) => {
    if (!dragging) return;
    // Swallowed either way — a reorder commits on drag end, and the browser must
    // not act on the drop inside a rich-text editor underneath
    e.preventDefault();
    e.stopPropagation();
    if (dragging.kind !== 'new') return;
    addSection(dragging.sectionType, gap);
    setDragging(null);
    setDropGap(null);
  };

  const handleDragEnd = () => {
    if (dragging?.kind === 'reorder' && dropGap !== null) {
      setLanguagePickerIndex(null);
      const from = dragging.index;
      setFormSections((prev) => {
        const next = [...prev];
        const [moved] = next.splice(from, 1);
        // Removing the dragged section shifts every gap after it down by one
        next.splice(dropGap > from ? dropGap - 1 : dropGap, 0, moved);
        return next;
      });
    }
    setDragging(null);
    setDropGap(null);
  };

  const handleImageUpload = async (index: number, file: File) => {
    try {
      setUploadingImageIndex(index);
      
      let articleId = editingArticle?.articleId;
      if (!articleId) {
        articleId = `temp-${Date.now()}`;
      }

      let accessToken = getAccessToken();
      if (isTokenExpired()) {
        await refreshAccessToken();
        accessToken = getAccessToken();
      }

      const formData = new FormData();
      formData.append('image', file);
      formData.append('articleId', articleId);

      const response = await fetch(`${API_URL}/api/admin/articles/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        updateSection(index, {
          imageKey: data.data.imageKey,
          imageUrl: data.data.imageUrl,
          ...(data.data.aspectRatio !== undefined && { aspectRatio: data.data.aspectRatio }),
        });
      } else {
        alert('Failed to upload image: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      alert('Failed to upload image');
    } finally {
      setUploadingImageIndex(null);
    }
  };

  const handleImage2Upload = async (index: number, file: File) => {
    try {
      setUploadingImage2Index(index);
      
      let articleId = editingArticle?.articleId;
      if (!articleId) {
        articleId = `temp-${Date.now()}`;
      }

      let accessToken = getAccessToken();
      if (isTokenExpired()) {
        await refreshAccessToken();
        accessToken = getAccessToken();
      }

      const formData = new FormData();
      formData.append('image', file);
      formData.append('articleId', articleId);

      const response = await fetch(`${API_URL}/api/admin/articles/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        updateSection(index, { imageKey2: data.data.imageKey, imageUrl2: data.data.imageUrl });
      } else {
        alert('Failed to upload image: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error uploading image 2:', err);
      alert('Failed to upload image');
    } finally {
      setUploadingImage2Index(null);
    }
  };

  const handleVideoUpload = async (index: number, file: File) => {
    try {
      setUploadingVideoIndex(index);
      
      let articleId = editingArticle?.articleId;
      if (!articleId) {
        articleId = `temp-${Date.now()}`;
      }

      let accessToken = getAccessToken();
      if (isTokenExpired()) {
        await refreshAccessToken();
        accessToken = getAccessToken();
      }

      const formData = new FormData();
      formData.append('video', file);
      formData.append('articleId', articleId);

      const response = await fetch(`${API_URL}/api/admin/articles/upload-video`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
        body: formData,
      });

      const data = await response.json();
      if (data.success) {
        updateSection(index, {
          videoKey: data.data.videoKey,
          videoUrl: data.data.videoUrl,
          ...(data.data.thumbnailKey && { thumbnailKey: data.data.thumbnailKey }),
          ...(data.data.thumbnailUrl && { thumbnailUrl: data.data.thumbnailUrl }),
          ...(data.data.aspectRatio !== undefined && { aspectRatio: data.data.aspectRatio }),
          // Absent when the probe could not tell; leaving it unset is what keeps the
          // app's mute control, so it must not be written as a `false`
          ...(data.data.hasAudio !== undefined && { hasAudio: data.data.hasAudio }),
        });
      } else {
        alert('Failed to upload video: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error uploading video:', err);
      alert('Failed to upload video');
    } finally {
      setUploadingVideoIndex(null);
    }
  };

  const selectExtension = (index: number, extension: ExtensionSearchResult) => {
    updateSection(index, { 
      extensionId: extension._id, 
      extensionName: extension.Name,
      extensionCreator: extension.Creator
    });
    setExtensionSearchQuery('');
    setExtensionSearchResults([]);
    setActiveExtensionSectionIndex(null);
  };

  const selectCreator = (index: number, creator: CreatorSearchResult) => {
    updateSection(index, { 
      creatorId: creator.userId, 
      creatorName: creator.name
    });
    setCreatorSearchQuery('');
    setCreatorSearchResults([]);
    setActiveCreatorSectionIndex(null);
  };

  const handleSubmit = async () => {
    const firstSection = formSections[0];
    if (!firstSection || firstSection.type !== 'articleCoverImage') {
      alert('First section must be a cover image');
      return;
    }
    const coverImage = firstSection as CoverImage;
    if (coverMediaType === 'image' && !coverImage.imageKey) {
      alert('Cover image is required');
      return;
    }
    if (coverMediaType === 'video' && !coverImage.videoKey) {
      alert('Cover video is required');
      return;
    }
    if (!coverImage.title?.trim()) {
      alert('Article title is required (English)');
      return;
    }

    const invalidSections = formSections.some((section, index) => {
      if (index === 0) return false;
      if (section.type === 'image' && !(section as ImageSection).imageKey) return true;
      if (section.type === 'paragraph' && !(section as ParagraphSection).content) return true;
      if (section.type === 'extensionCard' && !(section as ExtensionCardSection).extensionId) return true;
      return false;
    });

    if (invalidSections) {
      alert('Please fill in all section fields');
      return;
    }

    // A language added to a paragraph and left empty would silently show English
    // in that language — make the admin fill it in or remove it
    const emptyTranslations: string[] = [];
    formSections.forEach((section, index) => {
      if (section.type !== 'paragraph') return;
      const translations = (section as ParagraphSection).contentTranslations || {};
      for (const lang of TRANSLATION_LANGUAGES) {
        const html = translations[lang.code];
        if (html !== undefined && isBlankHtml(html)) {
          emptyTranslations.push(`Section ${index} (paragraph): ${lang.name}`);
        }
      }
    });
    if (emptyTranslations.length > 0) {
      alert(`These paragraph translations are empty — fill them in or remove them:\n\n${emptyTranslations.join('\n')}`);
      return;
    }

    try {
      setSubmitting(true);

      const sectionsToSubmit = formSections.map((section) => {
        if (section.type === 'articleCoverImage') {
          const cover = section as CoverImage;
          if (coverMediaType === 'image') {
            return { type: 'articleCoverImage', imageKey: cover.imageKey, title: cover.title?.trim(), titleTranslations: cleanTranslations(cover.titleTranslations), aspectRatio: cover.aspectRatio };
          } else {
            return { type: 'articleCoverImage', videoKey: cover.videoKey, thumbnailKey: cover.thumbnailKey, title: cover.title?.trim(), titleTranslations: cleanTranslations(cover.titleTranslations), aspectRatio: cover.aspectRatio, hasAudio: cover.hasAudio };
          }
        }
        if (section.type === 'image') {
          return { type: 'image', imageKey: (section as ImageSection).imageKey, imageKey2: (section as ImageSection).imageKey2, title: (section as ImageSection).title, aspectRatio: (section as ImageSection).aspectRatio };
        }
        if (section.type === 'paragraph') {
          const paragraph = section as ParagraphSection;
          return { type: 'paragraph', content: paragraph.content, contentTranslations: cleanTranslations(paragraph.contentTranslations) };
        }
        if (section.type === 'extensionCard') {
          const extSection = section as ExtensionCardSection;
          return { 
            type: 'extensionCard', 
            extensionId: extSection.extensionId,
            imageKey: extSection.imageKey,
          };
        }
        if (section.type === 'creatorCard') {
          const creatorSection = section as CreatorCardSection;
          return { 
            type: 'creatorCard', 
            creatorId: creatorSection.creatorId,
          };
        }
        if (section.type === 'video') {
          const videoSection = section as VideoSection;
          return { type: 'video', videoKey: videoSection.videoKey, thumbnailKey: videoSection.thumbnailKey, aspectRatio: videoSection.aspectRatio, hasAudio: videoSection.hasAudio };
        }
        return section;
      });

      const payload = {
        sections: sectionsToSubmit,
        startDate: startDate ? startDate.toISOString() : null,
        endDate: endDate ? endDate.toISOString() : null,
      };

      if (editingArticle) {
        await apiRequest(`/api/admin/articles/${editingArticle.articleId}`, {
          method: 'PUT',
          body: JSON.stringify(payload),
        });
      } else {
        await apiRequest('/api/admin/articles', {
          method: 'POST',
          body: JSON.stringify(payload),
        });
      }

      resetForm();
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error saving article:', err);
      alert('Failed to save article');
    } finally {
      setSubmitting(false);
    }
  };

  /** Thin strip between sections; during a drag it draws the line marking the drop position. */
  const renderDropZone = (gap: number) => (
    <div
      className={
        `section-drop-zone${dragging?.kind === 'new' ? ' drop-visible' : ''}` +
        `${dropGap === gap ? ' drop-hint' : ''}`
      }
      onDragOver={(e) => handleZoneDragOver(e, gap)}
      onDragLeave={() => handleZoneDragLeave(gap)}
      onDrop={(e) => handleDrop(e, gap)}
    />
  );

  // ---------------------------------------------------------------------------
  // Translations. English stays in the cover's `title` and each paragraph's
  // `content`; other languages go in `titleTranslations` / `contentTranslations`,
  // and the app shows English wherever a language has none.
  // ---------------------------------------------------------------------------

  /**
   * The title works like a home layout Section Title: the field is a button that
   * opens a translations modal, whose inputs write straight into the cover.
   * Done just closes; Cancel writes back the snapshot taken when it opened. The
   * field opens on both focus and click, so only the first (the one that finds
   * the modal closed) may snapshot — a second would capture the edits to undo.
   */
  const openTitleEditor = () => {
    if (!titleEditorOpen) {
      const cover = formSections[0] as CoverImage | undefined;
      titleSnapshot.current = { title: cover?.title, titleTranslations: cover?.titleTranslations };
    }
    setTitleEditorOpen(true);
  };

  const writeTitle = (title: string | undefined, titleTranslations: Translations | undefined) =>
    setFormSections((prev) => prev.map((section, i) => (
      i === 0 ? { ...section, title, titleTranslations } as AllSectionTypes : section
    )));

  const cancelTitleEditor = () => {
    writeTitle(titleSnapshot.current.title, titleSnapshot.current.titleTranslations);
    setTitleEditorOpen(false);
  };

  const renderTitleField = (cover: CoverImage) => (
    <button
      type="button"
      className={`hl-input hl-qa-label-btn hl-title-btn${cover.title ? '' : ' empty'}`}
      title="Edit article title translations"
      onFocus={openTitleEditor}
      onClick={openTitleEditor}
    >
      {cover.title || 'Article title * — click to enter it and its translations'}
    </button>
  );

  const renderTitleModal = () => {
    if (!titleEditorOpen) return null;
    const cover = formSections[0] as CoverImage | undefined;
    if (!cover || cover.type !== 'articleCoverImage') return null;
    const valueFor = (code: string) => (code === 'en' ? cover.title : cover.titleTranslations?.[code]) || '';
    const onTextChange = (code: string, text: string) => {
      if (code === 'en') writeTitle(text, cover.titleTranslations);
      else writeTitle(cover.title, { ...(cover.titleTranslations || {}), [code]: text });
    };

    return (
      <div className="hl-modal-overlay hl-label-overlay">
        <div className="hl-modal hl-label-modal" onClick={(e) => e.stopPropagation()}>
          <h2 className="hl-modal-title">Article title translations</h2>
          <div className="hl-modal-body">
            <table className="hl-label-table">
              <thead>
                <tr>
                  <th>Language</th>
                  <th>Text</th>
                </tr>
              </thead>
              <tbody>
                {LABEL_LANGUAGES.map((lang) => (
                  <tr key={lang.code}>
                    <td className="hl-label-lang">{lang.name}{lang.required ? ' *' : ''}</td>
                    <td>
                      <input
                        type="text"
                        className="hl-input"
                        placeholder={lang.required ? 'Required' : 'Falls back to English'}
                        value={valueFor(lang.code)}
                        onChange={(e) => onTextChange(lang.code, e.target.value)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <small className="hl-hint">English is required — the app shows it for any language left empty.</small>
          </div>
          <div className="hl-modal-actions">
            <button type="button" className="hl-cancel-btn" onClick={cancelTitleEditor}>
              Cancel
            </button>
            <button type="button" className="hl-confirm-btn" onClick={() => setTitleEditorOpen(false)}>
              Done
            </button>
          </div>
        </div>
      </div>
    );
  };

  const setParagraphTranslations = (index: number, contentTranslations: Translations) =>
    setFormSections((prev) => prev.map((section, i) => (
      i === index ? { ...section, contentTranslations } as AllSectionTypes : section
    )));

  /** English editor first, then one per added language, then the + to add another. */
  const renderParagraphEditor = (section: ParagraphSection, index: number) => {
    const translations = section.contentTranslations || {};
    const added = TRANSLATION_LANGUAGES.filter((lang) => translations[lang.code] !== undefined);
    const available = TRANSLATION_LANGUAGES.filter((lang) => translations[lang.code] === undefined);
    const pickerOpen = languagePickerIndex === index;

    return (
      <div className="paragraph-languages">
        <div className="paragraph-language">
          <div className="paragraph-language-header">
            <span className="paragraph-language-name">English</span>
          </div>
          <RichTextEditor
            value={section.content}
            onChange={(html) => updateSection(index, { content: html })}
            placeholder="Enter paragraph content *"
          />
        </div>

        {added.map((lang) => (
          <div key={lang.code} className="paragraph-language">
            <div className="paragraph-language-header">
              <span className="paragraph-language-name">{lang.name}</span>
              <button
                type="button"
                className="section-action-btn danger"
                title={`Remove the ${lang.name} translation — the app shows English instead`}
                onClick={() => {
                  const rest = { ...translations };
                  delete rest[lang.code];
                  setParagraphTranslations(index, rest);
                }}
              >
                <FiX />
              </button>
            </div>
            <RichTextEditor
              value={translations[lang.code] || ''}
              onChange={(html) => setParagraphTranslations(index, { ...translations, [lang.code]: html })}
              placeholder={`${lang.name} paragraph content`}
            />
          </div>
        ))}

        {available.length > 0 && (
          <div className="paragraph-language-add" ref={pickerOpen ? languagePickerRef : undefined}>
            <button
              type="button"
              className={`paragraph-language-add-btn${pickerOpen ? ' active' : ''}`}
              title="Add a translation of this paragraph"
              aria-haspopup="menu"
              aria-expanded={pickerOpen}
              onClick={() => setLanguagePickerIndex(pickerOpen ? null : index)}
            >
              <FiPlus /> Language
            </button>
            {pickerOpen && (
              <div className="paragraph-language-menu" role="menu" ref={languageMenuRef}>
                {available.map((lang) => (
                  <button
                    key={lang.code}
                    type="button"
                    role="menuitem"
                    className="paragraph-language-option"
                    onClick={() => {
                      setParagraphTranslations(index, { ...translations, [lang.code]: '' });
                      setLanguagePickerIndex(null);
                    }}
                  >
                    {lang.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderSectionEditor = (section: AllSectionTypes, index: number) => {
    const isCoverImage = index === 0 && section.type === 'articleCoverImage';
    const isDragging = dragging?.kind === 'reorder' && dragging.index === index;

    return (
      <div
        key={index}
        className={`section-editor ${isDragging ? 'dragging' : ''}`}
        onDragOver={(e) => handleSectionDragOver(e, index)}
      >
        <div
          className={`section-header${isCoverImage ? '' : ' draggable'}`}
          draggable={!isCoverImage}
          onDragStart={(e) => handleReorderStart(e, index)}
          onDragEnd={handleDragEnd}
        >
          <span className="section-type-badge">
            {section.type === 'articleCoverImage' && <>{coverMediaType === 'video' ? <FiVideo /> : <FiImage />} Cover {coverMediaType === 'video' ? 'Video' : 'Image'}</>}
            {section.type === 'image' && <><FiImage /> Image</>}
            {section.type === 'paragraph' && <><FiType /> Paragraph</>}
            {section.type === 'extensionCard' && <><FiGrid /> Lens</>}
            {section.type === 'creatorCard' && <><FiEdit2 /> Creator</>}
            {section.type === 'video' && <><FiVideo /> Video</>}
          </span>
          <div className="section-actions">
            <button
              type="button"
              onClick={() => moveSection(index, 'up')}
              disabled={index <= 1}
              className="section-action-btn"
            >
              <FiChevronUp />
            </button>
            <button
              type="button"
              onClick={() => moveSection(index, 'down')}
              disabled={index === formSections.length - 1 || index === 0}
              className="section-action-btn"
            >
              <FiChevronDown />
            </button>
            <button
              type="button"
              onClick={() => removeSection(index)}
              disabled={isCoverImage}
              className="section-action-btn danger"
              title={isCoverImage ? 'Cover image cannot be removed' : ''}
            >
              <FiTrash2 />
            </button>
          </div>
        </div>

        <div className="section-content">
          {(section.type === 'articleCoverImage' || section.type === 'image') && (
            <>
              {section.type === 'articleCoverImage' && (
                <div className="cover-media-toggle" style={{ display: 'flex', gap: 16, marginBottom: 12 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="coverMediaType"
                      checked={coverMediaType === 'image'}
                      onChange={() => setCoverMediaType('image')}
                    />
                    <FiImage /> Image
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="coverMediaType"
                      checked={coverMediaType === 'video'}
                      onChange={() => setCoverMediaType('video')}
                    />
                    <FiVideo /> Video
                  </label>
                </div>
              )}
              <div className={section.type === 'image' ? 'image-upload-row' : ''}>
                {(section.type === 'image' || (section.type === 'articleCoverImage' && coverMediaType === 'image')) && (
                  <div className="image-upload-area" style={{ position: 'relative', flex: 1 }}>
                    {section.type === 'image' && <div className="image-slot-label">Image 1 (required)</div>}
                    {section.imageUrl ? (
                      <div className="image-preview">
                        <img src={section.imageUrl} alt="Preview" />
                        <button
                          type="button"
                          onClick={() => updateSection(index, { imageKey: '', imageUrl: undefined })}
                          className="remove-image-btn"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    ) : (
                      <label className="image-upload-label">
                        <FiImage size={32} />
                        <span>Click to upload image</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleImageUpload(index, file);
                          }}
                          hidden
                        />
                      </label>
                    )}
                    {uploadingImageIndex === index && (
                      <div className="uploading-indicator" />
                    )}
                  </div>
                )}
                {section.type === 'articleCoverImage' && coverMediaType === 'video' && (
                  <div className="image-upload-area" style={{ position: 'relative', flex: 1 }}>
                    {(section as CoverImage).videoUrl ? (
                      <div className="image-preview">
                        <video src={(section as CoverImage).videoUrl} controls style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 6 }} />
                        <button
                          type="button"
                          onClick={() => updateSection(index, { videoKey: '', videoUrl: undefined })}
                          className="remove-image-btn"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    ) : (
                      <label className="image-upload-label">
                        <FiVideo size={32} />
                        <span>Click to upload cover video (max 100MB)</span>
                        <input
                          type="file"
                          accept="video/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleVideoUpload(index, file);
                          }}
                          hidden
                        />
                      </label>
                    )}
                    {uploadingVideoIndex === index && (
                      <div className="uploading-indicator" />
                    )}
                  </div>
                )}
                {section.type === 'image' && (
                  <div className="image-upload-area" style={{ position: 'relative', flex: 1 }}>
                    <div className="image-slot-label">Image 2 (optional, long press showing original photo)</div>
                    {(section as ImageSection).imageUrl2 ? (
                      <div className="image-preview">
                        <img src={(section as ImageSection).imageUrl2} alt="Preview 2" />
                        <button
                          type="button"
                          onClick={() => updateSection(index, { imageKey2: '', imageUrl2: undefined })}
                          className="remove-image-btn"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    ) : (
                      <label className="image-upload-label">
                        <FiImage size={24} />
                        <span>Click to upload right image</span>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) handleImage2Upload(index, file);
                          }}
                          hidden
                        />
                      </label>
                    )}
                    {uploadingImage2Index === index && (
                      <div className="uploading-indicator" />
                    )}
                  </div>
                )}
              </div>
              {section.type === 'articleCoverImage' && renderTitleField(section as CoverImage)}
              {section.type === 'image' && (
                <input
                  type="text"
                  placeholder="Image title (optional)"
                  value={(section as ImageSection).title || ''}
                  onChange={(e) => updateSection(index, { title: e.target.value })}
                  className="section-input"
                />
              )}
            </>
          )}

          {section.type === 'paragraph' && renderParagraphEditor(section as ParagraphSection, index)}

          {section.type === 'extensionCard' && (
            <>
              <div className="extension-selector">
                {section.extensionId ? (
                  <div className="selected-extension">
                    <div className="selected-extension-info">
                      <span className="extension-name">{section.extensionName || 'Unknown Extension'}</span>
                      {section.extensionCreator && <span className="extension-creator">by {section.extensionCreator}</span>}
                    </div>
                    <button
                      type="button"
                      onClick={() => updateSection(index, { extensionId: '', extensionName: undefined, extensionCreator: undefined })}
                      className="remove-extension-btn"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                ) : (
                  <div className="extension-search">
                    <input
                      type="text"
                      placeholder="Search lenses..."
                      value={activeExtensionSectionIndex === index ? extensionSearchQuery : ''}
                      onChange={(e) => {
                        setActiveExtensionSectionIndex(index);
                        setExtensionSearchQuery(e.target.value);
                      }}
                      onFocus={() => setActiveExtensionSectionIndex(index)}
                      className="section-input"
                    />
                    {activeExtensionSectionIndex === index && extensionSearchResults.length > 0 && (
                      <div className="extension-search-results">
                        {extensionSearchResults.map((ext) => (
                          <div
                            key={ext._id}
                            className="extension-search-item"
                            onClick={() => selectExtension(index, ext)}
                          >
                            {ext.thumbnailImageUrl && (
                              <img src={ext.thumbnailImageUrl} alt={ext.Name} className="extension-search-thumb" />
                            )}
                            <div className="extension-search-info">
                              <span className="extension-name">{ext.Name}</span>
                              <span className="extension-creator">by {ext.Creator}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {activeExtensionSectionIndex === index && searchingExtensions && (
                      <div className="extension-search-loading">Searching...</div>
                    )}
                  </div>
                )}
              </div>
              <div className="image-upload-area" style={{ position: 'relative' }}>
                {section.imageUrl ? (
                  <div className="image-preview">
                    <img src={section.imageUrl} alt="Lens preview" />
                    <button
                      type="button"
                      onClick={() => updateSection(index, { imageKey: '', imageUrl: undefined })}
                      className="remove-image-btn"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                ) : (
                  <label className="image-upload-label">
                    <FiImage size={32} />
                    <span>Click to upload lens image</span>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) handleImageUpload(index, file);
                      }}
                      hidden
                    />
                  </label>
                )}
                {uploadingImageIndex === index && (
                  <div className="uploading-indicator" />
                )}
              </div>
            </>
          )}

          {section.type === 'creatorCard' && (
            <div className="creator-selector">
              {section.creatorId ? (
                <div className="selected-extension">
                  <div className="selected-extension-info">
                    <span className="extension-name">{section.creatorName || 'Unknown Creator'}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => updateSection(index, { creatorId: '', creatorName: undefined })}
                    className="remove-extension-btn"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              ) : (
                <div className="extension-search">
                  <input
                    type="text"
                    placeholder="Search creators..."
                    value={activeCreatorSectionIndex === index ? creatorSearchQuery : ''}
                    onChange={(e) => {
                      setActiveCreatorSectionIndex(index);
                      setCreatorSearchQuery(e.target.value);
                    }}
                    onFocus={() => setActiveCreatorSectionIndex(index)}
                    className="section-input"
                  />
                  {activeCreatorSectionIndex === index && creatorSearchResults.length > 0 && (
                    <div className="extension-search-results">
                      {creatorSearchResults.map((creator) => (
                        <div
                          key={creator.userId}
                          className="extension-search-item"
                          onClick={() => selectCreator(index, creator)}
                        >
                          {creator.avatar && (
                            <img src={creator.avatar} alt={creator.name} className="extension-search-thumb" />
                          )}
                          <div className="extension-search-info">
                            <span className="extension-name">{creator.name}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {activeCreatorSectionIndex === index && searchingCreators && (
                    <div className="extension-search-loading">Searching...</div>
                  )}
                </div>
              )}
            </div>
          )}

          {section.type === 'video' && (
            <div className="image-upload-area" style={{ position: 'relative' }}>
              {(section as VideoSection).videoUrl ? (
                <div className="image-preview">
                  <video src={(section as VideoSection).videoUrl} controls style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 6 }} />
                  <button
                    type="button"
                    onClick={() => updateSection(index, { videoKey: '', videoUrl: undefined })}
                    className="remove-image-btn"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              ) : (
                <label className="image-upload-label">
                  <FiVideo size={32} />
                  <span>Click to upload video (max 100MB)</span>
                  <input
                    type="file"
                    accept="video/*"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleVideoUpload(index, file);
                    }}
                    hidden
                  />
                </label>
              )}
              {uploadingVideoIndex === index && (
                <div className="uploading-indicator" />
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  const coverSection = formSections[0];
  const bodySections = formSections.slice(1);

  return (
    <div className="modal-overlay">
      <div className="modal-content article-modal" onClick={(e) => e.stopPropagation()}>
        <h2>{editingArticle ? 'Edit Article' : 'Create Article'}</h2>

        <div className="article-form-columns">
          {/* Left column: what can be added to the article */}
          <div className="article-form-column article-form-sidebar">
            <div className="form-group">
              <label>Add Section</label>
              <div className="add-section-buttons add-section-buttons-stacked">
                {SECTION_OPTIONS.map((option) => (
                  <button
                    key={option.type}
                    type="button"
                    className="add-section-btn add-section-draggable"
                    title="Drag into the article, or click to add at the end"
                    draggable
                    onDragStart={(e) => handleNewDragStart(e, option.type)}
                    onDragEnd={handleDragEnd}
                    onClick={() => addSection(option.type)}
                  >
                    {option.icon} {option.label}
                  </button>
                ))}
              </div>
              <small className="field-hint">
                Drag a section into the article to place it, or click to add it at the end. Drag a
                section's header to move it.
              </small>
            </div>
          </div>

          {/* Right column: the article itself — cover, schedule, and (content only) body sections */}
          <div className="article-form-column article-form-main">
            <div className="sections-container">
              <div className="sections-header">
                <label>Cover</label>
              </div>
              <div className="sections-list">
                {coverSection && renderSectionEditor(coverSection, 0)}
              </div>
            </div>

            <div className="form-group">
              <label>Start Date & Time (optional)</label>
              <DatePicker
                selected={startDate}
                onChange={(date: Date | null) => setStartDate(date)}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat="yyyy-MM-dd HH:mm"
                timeCaption="Time"
                placeholderText="Select start date & time"
                className="datepicker-input"
                isClearable
                // The right column is a scroll container — fixed positioning keeps
                // the calendar from being clipped by it
                popperProps={{ strategy: 'fixed' }}
              />
              <small className="field-hint">Leave empty to show immediately</small>
            </div>

            <div className="form-group">
              <label>End Date & Time (optional)</label>
              <DatePicker
                selected={endDate}
                onChange={(date: Date | null) => setEndDate(date)}
                showTimeSelect
                timeFormat="HH:mm"
                timeIntervals={15}
                dateFormat="yyyy-MM-dd HH:mm"
                timeCaption="Time"
                placeholderText="Select end date & time"
                minDate={startDate || undefined}
                className="datepicker-input"
                isClearable
                popperProps={{ strategy: 'fixed' }}
              />
              <small className="field-hint">Leave empty for no end date</small>
            </div>

            <div className="sections-container sections-container-dnd">
              <div className="sections-header">
                <label>Sections</label>
              </div>
              {/* Drop strips sit between the sections and supply their vertical rhythm.
                  A drop that misses a strip falls through to this container and lands
                  at whichever gap the hover already picked. */}
              <div
                className={`sections-list sections-list-dnd${dragging?.kind === 'new' ? ' drop-ready' : ''}`}
                onDragOver={(e) => { if (dragging) e.preventDefault(); }}
                onDragLeave={(e) => {
                  // Dragging back out of the list cancels the drop position, so
                  // releasing elsewhere leaves the article untouched
                  if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setDropGap(null);
                }}
                onDrop={(e) => handleDrop(e, dropGap ?? formSections.length)}
              >
                {renderDropZone(1)}
                {bodySections.length === 0 ? (
                  <div
                    className={`empty-sections${dropGap === 1 ? ' drop-hint' : ''}`}
                    onDragOver={(e) => handleZoneDragOver(e, 1)}
                    onDragLeave={() => handleZoneDragLeave(1)}
                    onDrop={(e) => handleDrop(e, 1)}
                  >
                    Drag a section here, or add one from the panel on the left
                  </div>
                ) : (
                  bodySections.map((section, index) => (
                    <Fragment key={index + 1}>
                      {renderSectionEditor(section, index + 1)}
                      {renderDropZone(index + 2)}
                    </Fragment>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button type="button" onClick={onClose} className="cancel-btn" disabled={submitting}>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} className="submit-btn" disabled={submitting}>
            {submitting ? 'Saving...' : editingArticle ? 'Update Article' : 'Create Article'}
          </button>
        </div>
      </div>
      {renderTitleModal()}
    </div>
  );
}
